import { useTranslation } from 'react-i18next';
import {
  SubformComponent,
  SubCardFieldsComponent,
  SubModule,
} from '@/modules/admin/audits/components/submodules/types';
import { LineText } from '@common/components/fragments/LineText';
import AuditsExportsTupDocumentsExport from '@/app/spraypaint/audits/exports/AuditsExportsTupDocumentsExport';
import { ControlledSelect } from '@common/components/inputs/controlledInput/ControlledSelect';
import useLegalEntitiesSelectOptions from '@/modules/common/legalEntity/useLegalEntitiesSelectOptions';
import useLegalEntities from '@common/legalEntity/useLegalEntities';

export const jsonApiType = AuditsExportsTupDocumentsExport.jsonapiType;

export type Values = {
  legalEntityId: AuditsExportsTupDocumentsExport['legalEntityId'];
  deleteOldFiles: AuditsExportsTupDocumentsExport['deleteOldFiles'];
};

export const defaultValues: Values = {
  legalEntityId: '1',
  deleteOldFiles: false,
};

function createExportStrategy(data: Values): AuditsExportsTupDocumentsExport {
  return new AuditsExportsTupDocumentsExport(data);
}

export const FormComponent: SubformComponent = ({ control }) => {
  const { t } = useTranslation();
  const legalEntityOptions = useLegalEntitiesSelectOptions();

  return (
    <>
      <ControlledSelect
        label={t('views.legal_entity.label')}
        className="mt-20"
        control={control}
        defaultValue={undefined}
        name="exportStrategy.params.legalEntityId"
        options={legalEntityOptions}
        required
      />
      <ControlledSelect
        label={t(
          'activerecord.attributes.audits/exports/tup_documents_export.delete_old_files',
          {
            ns: 'rails',
          },
        )}
        className="mt-20"
        control={control}
        defaultValue={undefined}
        name="exportStrategy.params.deleteOldFiles"
        options={[
          { label: t('views.shared.positive'), value: true },
          { label: t('views.shared.negative'), value: false },
        ]}
      />
    </>
  );
};

export const CardFieldsComponent: SubCardFieldsComponent<
  AuditsExportsTupDocumentsExport
> = ({ strategy }) => {
  const { t } = useTranslation();
  const { legalEntityId } = strategy;
  const { legalEntities } = useLegalEntities();
  const legalEntity = (legalEntities || []).find(
    ({ id }) => String(id) === String(legalEntityId),
  );

  return (
    <>
      <LineText
        label={t('activerecord.attributes.audits/export.export_strategy', {
          ns: 'rails',
        })}
        value={t('activerecord.models.audits/exports/tup_documents_exports', {
          ns: 'rails',
        })}
      />
      <LineText
        label={t(
          'activerecord.attributes.audits/exports/tup_documents_export.legal_entity_id',
          {
            ns: 'rails',
          },
        )}
        value={legalEntity?.name}
      />
      <LineText
        label={t(
          'activerecord.attributes.audits/exports/tup_documents_export.delete_old_files',
          {
            ns: 'rails',
          },
        )}
        value={
          strategy.deleteOldFiles
            ? t('views.shared.positive')
            : t('views.shared.negative')
        }
      />
    </>
  );
};

export const AuditsExportsTupDocumentsExportSubmodule: SubModule<
  Values,
  AuditsExportsTupDocumentsExport
> = {
  jsonApiType,
  defaultValues,
  FormComponent,
  CardFieldsComponent,
  createExportStrategy,
};
